import { mechanicChoices, memberName } from "@szerviz/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import type { Member } from "../data";
import { Help } from "./Help";

/** Members of the workshop (names for the pickers, the list and the printed sheet); null while loading. */
export function useMembers(): Member[] | null {
  const { store, workshop } = useApp();
  const [members, setMembers] = useState<Member[] | null>(null);
  useEffect(() => {
    let live = true;
    store.listMembers(workshop.id).then((m) => live && setMembers(m), () => live && setMembers([]));
    return () => {
      live = false;
    };
  }, [store, workshop.id]);
  return members;
}

/** The name of a member by id; a former member (no longer in the list) is "earlier user". */
export function useMechanicName(members: Member[] | null) {
  const { t } = useTranslation();
  return (id: string | null | undefined): string | null => {
    if (!id) return null;
    const m = members?.find((x) => x.userId === id);
    return m ? memberName(m) : members ? t("mechanic.former") : null;
  };
}

/** More than this many choices: a list instead of cards (a big workshop). */
const MAX_CARDS = 8;

/**
 * Chooses a mechanic with big cards and a round selector (an either-or choice, phone first): the mechanics first,
 * then owners and managers. `none` is the empty choice: "Nincs kiválasztva" on the work order, "same as the
 * responsible mechanic" on a labour line.
 */
export function MechanicPicker({ name, label, value, onChange, members, none, help, invalid, required }: {
  /** Radio group name (unique on the page). */
  name: string;
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
  members: Member[] | null;
  /** Text of the empty choice; without it there is none (a labour line of a work order with a responsible mechanic). */
  none?: string;
  help?: string;
  invalid?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const { userId: me } = useApp();
  const choices = mechanicChoices(members ?? []);
  // a former member stays visible while the work order still points to them
  const former = value && members && !choices.some((m) => m.userId === value) ? value : null;
  const caption = (
    <>
      {label}
      {required && <span className="req" aria-hidden="true"> *</span>}
      {help && <Help id={help} />}
    </>
  );
  if (members === null) return <p className="muted">{t("common.loading")}</p>;
  if (choices.length + 1 > MAX_CARDS) {
    return (
      <label className={`field wide${invalid ? " invalid" : ""}`}>
        <span className="field-label">{caption}</span>
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} aria-label={label} aria-invalid={invalid || undefined}>
          {none !== undefined && <option value="">{none}</option>}
          {choices.map((m) => (
            <option key={m.userId} value={m.userId}>
              {memberName(m)} · {t(`role.${m.role}.label`)}
            </option>
          ))}
          {former && <option value={former}>{t("mechanic.former")}</option>}
        </select>
      </label>
    );
  }
  return (
    <fieldset className={`choice-cards mechanic-cards wide${invalid ? " invalid" : ""}`} aria-invalid={invalid || undefined}>
      <legend>{caption}</legend>
      {choices.map((m) => (
        <label key={m.userId} className={`choice-card${value === m.userId ? " selected" : ""}`}>
          <input type="radio" name={name} checked={value === m.userId} onChange={() => onChange(m.userId)} />
          <span>
            <strong>{memberName(m)}</strong>
            {m.userId === me && <span className="muted"> ({t("users.you")})</span>}
            <br />
            <small className="muted">{t(`role.${m.role}.label`)}</small>
          </span>
        </label>
      ))}
      {former && (
        <label className="choice-card selected">
          <input type="radio" name={name} checked onChange={() => onChange(former)} />
          <span>
            <strong>{t("mechanic.former")}</strong>
          </span>
        </label>
      )}
      {none !== undefined && (
        <label className={`choice-card mechanic-none${value === null ? " selected" : ""}`}>
          <input type="radio" name={name} checked={value === null} onChange={() => onChange(null)} />
          <span>{none}</span>
        </label>
      )}
    </fieldset>
  );
}
